import { useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { auth, apiFetch, ssoProbe } from '../api'

type AuthProps = {
  onAuthenticated?: (user: any) => Promise<void> | void
}

function sanitizeReturnTo(value: string | null) {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return null
  }

  if (value.startsWith('//')) {
    return null
  }

  return value
}

function base64UrlToArrayBuffer(base64Url: string): ArrayBuffer {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

function serializeCredentialGetResponse(credential: any): string {
  const response = credential.response
  return JSON.stringify({
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle ? arrayBufferToBase64Url(response.userHandle) : null
    },
    type: credential.type
  })
}

function Auth({ onAuthenticated }: AuthProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [success, setSuccess] = useState<string | null>(null)
  const [signingInWithPasskey, setSigningInWithPasskey] = useState(false)
  const [step, setStep] = useState<'email' | 'password'>('email')
  const [ssoRedirecting, setSsoRedirecting] = useState(false)
  const [ssoLabel, setSsoLabel] = useState<string | null>(null)

  const handleEmailContinue = async () => {
    if (!email.trim()) return
    setLoading(true)
    setError(null)
    setSsoRedirecting(false)

    try {
      const result = await ssoProbe(email)
      if (result.type !== 'none' && result.redirectUrl) {
        setSsoLabel(result.label)
        setSsoRedirecting(true)
        window.location.href = result.redirectUrl
        return
      }
    } catch {
      // probe failed — fall through to password
    }

    setStep('password')
    setLoading(false)
  }

  const handleEmailBack = () => {
    setStep('email')
    setError(null)
    setPassword('')
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    if (mode === 'signin') {
      try {
        const user = await auth.login(email, password)
        if (user) {
          await onAuthenticated?.(user)
          const returnTo = sanitizeReturnTo(new URLSearchParams(location.search).get('returnTo'))
          navigate(returnTo || '/', { replace: true })
        }
      } catch (signInError: any) {
        setError(signInError.message)
      }
    } else {
      if (!username.trim()) {
        setError('Please choose a username.')
        setLoading(false)
        return
      }

      try {
        const data = await auth.register(email, password, username.trim().toLowerCase(), fullName.trim())
        setSuccess(data?.message || 'Account created. Please check your email to confirm your account.')
      } catch (signUpError: any) {
        setError(signUpError.message)
      }
    }
    setLoading(false)
  }

  const handleSignInWithPasskey = async () => {
    if (!email.trim()) {
      setError('Please enter your email address.')
      return
    }

    if (!window.PublicKeyCredential) {
      setError('Your browser does not support passkeys.')
      return
    }

    setSigningInWithPasskey(true)
    setError(null)
    try {
      const optionsData = await apiFetch('/api/auth/passkey/options', {
        method: 'POST',
        body: JSON.stringify({ email })
      }, false)

      const options = optionsData as any
      if (!options.challenge || !options.allowCredentials) {
        throw new Error('Failed to retrieve passkey options from server.')
      }

      options.challenge = base64UrlToArrayBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((cred: any) => ({
          ...cred,
          id: typeof cred.id === 'string' ? base64UrlToArrayBuffer(cred.id) : cred.id
        }))
      }

      const credential = await navigator.credentials.get({ publicKey: options }) as any
      if (!credential) {
        throw new Error('Passkey authentication was cancelled.')
      }

      const credentialJson = serializeCredentialGetResponse(credential)
      const user = await auth.loginWithPasskey(email, credentialJson)
      if (user) {
        await onAuthenticated?.(user)
        const returnTo = sanitizeReturnTo(new URLSearchParams(location.search).get('returnTo'))
        navigate(returnTo || '/', { replace: true })
      }
    } catch (err: any) {
      setError(
        err.message?.includes('NotAllowed')
          ? 'Passkey authentication was cancelled.'
          : err.message || 'Failed to sign in with passkey.'
      )
    } finally {
      setSigningInWithPasskey(false)
    }
  }

  const showPasswordStep = mode === 'signin' && step === 'password'
  const showEmailStep = mode === 'signin' && step === 'email'
  const isSignUp = mode === 'signup'

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand__name">CADENCE</div>
          <div className="auth-brand__sub">by Captus</div>
        </div>
        <h1 className="auth-headline">
          Bid management built for Government and Defence.
        </h1>
        <p className="auth-body">
          AI-powered opportunity intelligence, structured qualification,
          bid tracking, and response planning — in one platform built
          on fifteen years of sector expertise.
        </p>
      </div>

      <div className="auth-right">
        <div className="auth-form">
          <h2>{mode === 'signin' ? 'Sign in to Cadence' : 'Create your account'}</h2>
          <p>
            {mode === 'signin'
              ? 'Enter your credentials to access your bid workspace.'
              : 'Set up your Cadence account to get started.'}
          </p>

          {error && <div className="error-msg">{error}</div>}
          {success && <div className="success-msg">{success}</div>}

          {ssoRedirecting && (
            <div className="success-msg">
              Redirecting to {ssoLabel || 'your organisation\'s sign-in page'}…
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {isSignUp && (
              <>
                <div className="form-group">
                  <label>Full Name (optional)</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={event => setFullName(event.target.value)}
                    placeholder="e.g. Jonathan Phillpotts"
                  />
                </div>
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={username}
                    onChange={event => setUsername(event.target.value.replace(/\s/g, '').toLowerCase())}
                    placeholder="e.g. jonathanp"
                  />
                  <div style={{ fontSize: '0.75rem', color: 'var(--slate)', marginTop: '4px' }}>
                    Lowercase letters and numbers only. This is how you will appear in Cadence.
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                placeholder="your@email.com"
                required
                disabled={ssoRedirecting}
              />
            </div>

            {isSignUp && (
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  placeholder="Create a password"
                  required
                />
              </div>
            )}

            {showPasswordStep && (
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  placeholder="Your password"
                  required
                />
              </div>
            )}

            {showEmailStep && !isSignUp && (
              <button
                type="button"
                className="btn btn--primary btn--full"
                disabled={loading || !email.trim()}
                onClick={handleEmailContinue}
              >
                {loading ? 'Checking…' : 'Continue'}
              </button>
            )}

            {(showPasswordStep || isSignUp) && (
              <button
                type="submit"
                className="btn btn--primary btn--full"
                disabled={loading}
              >
                {loading
                  ? 'Please wait...'
                  : mode === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            )}
          </form>

          {showPasswordStep && (
            <div style={{ marginTop: '8px', textAlign: 'center' }}>
              <button
                onClick={handleEmailBack}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--green)', fontWeight: 600,
                  cursor: 'pointer', fontSize: '0.875rem'
                }}
              >
                ← Back
              </button>
            </div>
          )}

          {mode === 'signin' && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--silver)' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--slate)', marginBottom: '12px', textAlign: 'center', textTransform: 'uppercase' }}>
                Or sign in with a passkey
              </div>
              <button
                type="button"
                onClick={handleSignInWithPasskey}
                disabled={signingInWithPasskey || ssoRedirecting}
                className="btn btn--secondary btn--full"
                style={{ marginBottom: '12px' }}
              >
                {signingInWithPasskey ? 'Authenticating…' : '🔐 Sign in with Passkey'}
              </button>
              <p style={{ fontSize: '0.7rem', color: 'var(--slate)', textAlign: 'center' }}>
                You'll be prompted to authenticate using your device or security key.
              </p>
            </div>
          )}

          <p style={{
            marginTop: '24px', fontSize: '0.875rem',
            color: 'var(--slate)', textAlign: 'center'
          }}>
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => {
                setMode(mode === 'signin' ? 'signup' : 'signin')
                setError(null)
                setSuccess(null)
                setStep('email')
                setPassword('')
              }}
              style={{
                background: 'none', border: 'none',
                color: 'var(--green)', fontWeight: 600,
                cursor: 'pointer', fontSize: '0.875rem'
              }}
            >
              {mode === 'signin' ? 'Create account' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default Auth
